import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  ADMIN_ALERT_CHANNELS,
  ADMIN_ALERT_COMPARATORS,
  ADMIN_ALERT_METRICS,
  ADMIN_ALERT_SEVERITIES,
  AdminAlertChannel,
  AdminAlertComparator,
  AdminAlertMetric,
  AdminAlertRule,
  AdminAlertSeverity,
} from "@equipped/core/backend/client";
import { useAuth } from "../auth/AuthContext";
import { MainCard } from "../components/MainCard";

/** Friendly labels for the metric picker — falls back to the raw id for any metric
 * this page doesn't know about yet (forward-compat, docs/108-style). */
const METRIC_LABELS: Record<string, string> = {
  "cf.quotaPercent": "Cloudflare quota used (%)",
  "cf.requestCount": "Cloudflare Worker requests",
  "cf.d1RowsRead": "D1 rows read",
  "cf.d1RowsWritten": "D1 rows written",
  "cf.r2StorageBytes": "R2 storage (bytes)",
  "app.errorRatePercent": "App error rate (%) — not yet gathered (v1)",
  "app.activeUsers": "Active users (in the window)",
  "app.newUsers": "New signups (in the window)",
  "app.usageCounter": "A usage counter (listens/exports/prints/…)",
  "system.uptimeDown": "Uptime down — not yet gathered (v1/v2)",
};

const COMPARATOR_LABELS: Record<AdminAlertComparator, string> = { gt: ">", gte: "≥", lt: "<", lte: "≤" };

interface RuleFormState {
  id?: string;
  label: string;
  metric: AdminAlertMetric;
  metricKey: string;
  comparator: AdminAlertComparator;
  threshold: string;
  windowMinutes: string;
  channels: AdminAlertChannel[];
  cooldownMinutes: string;
  consecutiveBreaches: string;
  severity: AdminAlertSeverity;
  enabled: boolean;
}

const EMPTY_FORM: RuleFormState = {
  label: "",
  metric: "app.newUsers",
  metricKey: "",
  comparator: "gt",
  threshold: "",
  windowMinutes: "60",
  channels: ["inApp"],
  cooldownMinutes: "30",
  consecutiveBreaches: "2",
  severity: "warning",
  enabled: false,
};

function formToInput(form: RuleFormState) {
  return {
    id: form.id,
    label: form.label.trim(),
    metric: form.metric,
    metricKey: form.metricKey.trim() || null,
    comparator: form.comparator,
    threshold: Number(form.threshold),
    windowMinutes: Math.max(1, Math.floor(Number(form.windowMinutes) || 1)),
    channels: form.channels,
    cooldownMinutes: Math.max(0, Math.floor(Number(form.cooldownMinutes) || 0)),
    consecutiveBreaches: Math.max(1, Math.floor(Number(form.consecutiveBreaches) || 1)),
    severity: form.severity,
    enabled: form.enabled,
  };
}

function ruleToForm(rule: AdminAlertRule): RuleFormState {
  return {
    id: rule.id,
    label: rule.label,
    metric: rule.metric,
    metricKey: rule.metricKey ?? "",
    comparator: rule.comparator,
    threshold: String(rule.threshold),
    windowMinutes: String(rule.windowMinutes),
    channels: rule.channels,
    cooldownMinutes: String(rule.cooldownMinutes),
    consecutiveBreaches: String(rule.consecutiveBreaches),
    severity: rule.severity,
    enabled: rule.enabled,
  };
}

/**
 * Alerts config page (docs/347-ADMIN-ALERTS-AND-MONITORING.md v0 §6) — a threshold-rule
 * table + a create/edit form. Every seeded/newly-created rule ships `enabled:false` (the
 * owner "opts in" via the Enabled toggle) — nothing fires until the owner explicitly
 * turns a rule on.
 */
export function AlertsPage() {
  const { client, token } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<RuleFormState>(EMPTY_FORM);

  const rulesQuery = useQuery({
    queryKey: ["admin-alert-rules"],
    queryFn: () => client!.adminAlertRules(token!),
    enabled: !!client && !!token,
  });
  const rules = rulesQuery.data ?? [];

  const saveMutation = useMutation({
    mutationFn: () => client!.adminSaveAlertRule(token!, formToInput(form)),
    onSuccess: (rule) => {
      queryClient.setQueryData<AdminAlertRule[]>(["admin-alert-rules"], (list) => [
        rule,
        ...(list ?? []).filter((r) => r.id !== rule.id),
      ]);
      setForm(EMPTY_FORM);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => client!.adminDeleteAlertRule(token!, id),
    onSuccess: (_ok, id) => {
      queryClient.setQueryData<AdminAlertRule[]>(["admin-alert-rules"], (list) => (list ?? []).filter((r) => r.id !== id));
    },
  });

  const toggleEnabledMutation = useMutation({
    mutationFn: (rule: AdminAlertRule) =>
      client!.adminSaveAlertRule(token!, { ...formToInput(ruleToForm(rule)), enabled: !rule.enabled }),
    onSuccess: (rule) => {
      queryClient.setQueryData<AdminAlertRule[]>(["admin-alert-rules"], (list) =>
        (list ?? []).map((r) => (r.id === rule.id ? rule : r)),
      );
    },
  });

  const toggleChannel = (channel: AdminAlertChannel) =>
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(channel) ? f.channels.filter((c) => c !== channel) : [...f.channels, channel],
    }));

  const saveError = saveMutation.isError ? (saveMutation.error as Error).message : null;

  return (
    <>
      <MainCard title={form.id ? "Edit alert rule" : "Create an alert rule"} sx={{ mb: 2 }}>
        <Stack spacing={1.5}>
          <TextField
            size="small"
            label="Label"
            placeholder="e.g. New signups spike"
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
          />
          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              select
              label="Metric"
              value={form.metric}
              onChange={(e) => setForm((f) => ({ ...f, metric: e.target.value as AdminAlertMetric }))}
              fullWidth
            >
              {ADMIN_ALERT_METRICS.map((m) => (
                <MenuItem key={m} value={m}>
                  {METRIC_LABELS[m] ?? m}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Metric key (optional)"
              placeholder="e.g. a usage-counter name"
              value={form.metricKey}
              onChange={(e) => setForm((f) => ({ ...f, metricKey: e.target.value }))}
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              select
              label="Comparator"
              value={form.comparator}
              onChange={(e) => setForm((f) => ({ ...f, comparator: e.target.value as AdminAlertComparator }))}
              sx={{ minWidth: 100 }}
            >
              {ADMIN_ALERT_COMPARATORS.map((c) => (
                <MenuItem key={c} value={c}>
                  {COMPARATOR_LABELS[c]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              type="number"
              label="Threshold"
              value={form.threshold}
              onChange={(e) => setForm((f) => ({ ...f, threshold: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label="Window (minutes)"
              value={form.windowMinutes}
              onChange={(e) => setForm((f) => ({ ...f, windowMinutes: e.target.value }))}
              fullWidth
            />
          </Stack>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Channels — "statusOnly" shows on the Monitoring dashboard but never delivers
            </Typography>
            <Stack direction="row" spacing={1}>
              {ADMIN_ALERT_CHANNELS.map((c) => (
                <Chip
                  key={c}
                  label={c}
                  color={form.channels.includes(c) ? "primary" : "default"}
                  variant={form.channels.includes(c) ? "filled" : "outlined"}
                  onClick={() => toggleChannel(c)}
                />
              ))}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              type="number"
              label="Cooldown (minutes)"
              value={form.cooldownMinutes}
              onChange={(e) => setForm((f) => ({ ...f, cooldownMinutes: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label="Consecutive breaches"
              value={form.consecutiveBreaches}
              onChange={(e) => setForm((f) => ({ ...f, consecutiveBreaches: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              select
              label="Severity"
              value={form.severity}
              onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value as AdminAlertSeverity }))}
              fullWidth
            >
              {ADMIN_ALERT_SEVERITIES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <FormControlLabel
            control={<Switch checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />}
            label="Enabled"
          />
          {saveError ? <Alert severity="error">{saveError}</Alert> : null}
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="contained"
              disabled={saveMutation.isPending || !form.label.trim() || form.channels.length === 0}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? <CircularProgress size={18} color="inherit" /> : form.id ? "Save changes" : "Create rule"}
            </Button>
            {form.id ? (
              <Button variant="outlined" color="secondary" onClick={() => setForm(EMPTY_FORM)}>
                Cancel
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </MainCard>

      <MainCard title="Alert rules">
        {rulesQuery.isError ? <Alert severity="error">{(rulesQuery.error as Error).message}</Alert> : null}
        {rulesQuery.isLoading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : rules.length === 0 ? (
          <Typography color="text.secondary" sx={{ mb: 0 }}>
            No alert rules yet — create one above (it starts disabled until you turn it on).
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {rules.map((rule) => (
              <Box
                key={rule.id}
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>
                    {rule.label} {rule.enabled ? null : <Chip size="small" label="disabled" sx={{ ml: 1 }} />}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {METRIC_LABELS[rule.metric] ?? rule.metric}
                    {rule.metricKey ? ` (${rule.metricKey})` : ""} {COMPARATOR_LABELS[rule.comparator]} {rule.threshold} ·{" "}
                    {rule.consecutiveBreaches} consecutive · {rule.cooldownMinutes}m cooldown · {rule.severity} ·{" "}
                    {rule.channels.join(", ")}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    color={rule.enabled ? "warning" : "success"}
                    disabled={toggleEnabledMutation.isPending && toggleEnabledMutation.variables?.id === rule.id}
                    onClick={() => toggleEnabledMutation.mutate(rule)}
                  >
                    {rule.enabled ? "Disable" : "Enable"}
                  </Button>
                  <Button size="small" variant="outlined" color="secondary" onClick={() => setForm(ruleToForm(rule))}>
                    Edit
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={deleteMutation.isPending && deleteMutation.variables === rule.id}
                    onClick={() => deleteMutation.mutate(rule.id)}
                  >
                    Delete
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </MainCard>
    </>
  );
}
